'use client';

import { useEffect, useState } from 'react';
import { postGQLQuery } from '@/lib/client';
import { Q_APP_CONTROL_PUBLIC } from '@/lib/mutations/app-control.mutation';
import { IAppControl, parseAppControlValue } from '@/lib/interface/app-control.interface';

/**
 * Reads a single PUBLIC app control by key. Safe to call from
 * unauthenticated pages (sign-up, reset password) — backend resolver
 * `appControlPublic` only returns controls flagged isPublic = true.
 *
 * @param key      The control key, e.g. 'auth.captchaEnabled'
 * @param fallback Value returned while loading or if the control is missing.
 */
export function useAppControlPublic<T = any>(key: string, fallback: T) {
  const [value, setValue] = useState<T>(fallback);
  const [control, setControl] = useState<IAppControl | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    postGQLQuery(Q_APP_CONTROL_PUBLIC, { input: { key } }, 'appControlPublic')
      .then((row: IAppControl | null) => {
        if (cancelled) return;
        setControl(row ?? null);
        setValue(parseAppControlValue<T>(row, fallback));
      })
      .catch(() => {
        if (cancelled) return;
        setControl(null);
        setValue(fallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { value, control, loading };
}

